import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  SupportLevelValue,
  useCloseIncident,
  useCreateIncident,
  useProductIncidents,
  useReopenIncident,
  useUpdateIncident,
} from '@data';
import { useOrganizationId } from '@app/utils/hooks';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { incidentPath } from '@app/constants/url';
import { Loader } from '@ui';
import { Button } from '@ui/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { SectionHeading } from '@app/components/core/cards';
import {
  IncidentDraft,
  IncidentPeriod,
  IncidentView,
  SupportAgreement,
  agreementForLevel,
  buildIncidentsView,
  defaultIncidentPeriod,
  emptyDraft,
  incidentFromApi,
  incidentInputFromDraft,
  incidentPeriods,
  resolutionPatch,
  splitIncidents,
} from './sources/incidents';
import IncidentCloseDialog from './IncidentCloseDialog';
import IncidentEditor from './IncidentEditor';
import IncidentResolveDialog from './IncidentResolveDialog';
import IncidentRowActions from './IncidentRowActions';
import { IncidentRow, OpenIncidentTable, ResolvedIncidentTable } from './IncidentTables';
import { Card } from './cards';

const eur = (amount: number): string =>
  new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);

/** Quarter picker for the Incidents tab. */
export const IncidentPeriodSelect = ({
  value,
  onChange,
  productId,
}: {
  value: IncidentPeriod;
  onChange: (period: IncidentPeriod) => void;
  productId?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const { data: incidentData } = useProductIncidents(productId);
  const incidents = useMemo(() => (incidentData?.items ?? []).map(incidentFromApi), [incidentData?.items]);
  const periods = incidentPeriods(incidents);
  // The quarter in progress belongs in the list: it is the one a new incident is logged against.
  const current = defaultIncidentPeriod();
  const withCurrent = periods.some(period => period.year === current.year && period.quarter === current.quarter)
    ? periods
    : [current, ...periods];

  const key = (period: IncidentPeriod): string => `${period.year}-${period.quarter}`;
  const options = withCurrent.map(period => ({
    value: key(period),
    label: t('incidents.quarterOf', { quarter: period.quarter, year: period.year }),
  }));

  return (
    <Select
      value={key(value)}
      onValueChange={next => {
        if (!next) return;
        const [year, quarter] = next.split('-').map(Number);
        onChange({ year, quarter: quarter as IncidentPeriod['quarter'] });
      }}
      items={selectItemsByValue(options)}
    >
      <SelectTrigger size="sm" className="bg-card shadow-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/** What the client bought: the window, what is covered around the clock, and what it costs a quarter. */
// The retainer is ours, not theirs, and a placeholder per level until the product carries its own: never a client's.
const AgreementCard = ({
  agreement,
  showsRetainer,
}: {
  agreement: SupportAgreement;
  showsRetainer: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div>
          <p className="text-2xl font-bold capitalize text-foreground">{t(`incidents.level.${agreement.level}`)}</p>
          <p className="mt-1 text-sm font-medium text-foreground">{t('incidents.supportAgreement')}</p>
          {showsRetainer && (
            <p className="text-xs text-muted-foreground">
              {t('incidents.retainerPerQuarter', { amount: eur(agreement.retainer) })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('incidents.serviceWindow')}</p>
            <p className="mt-0.5 font-medium text-foreground">{agreement.window}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('incidents.aroundTheClock')}</p>
            <p className="mt-0.5 font-medium text-foreground">
              {agreement.aroundTheClock.length ? agreement.aroundTheClock.join(', ') : t('incidents.noneAroundClock')}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

/** Support and incidents for one quarter: what the client is owed, what came in, and whether we kept to it. */
const IncidentsTab = ({
  period,
  supportLevel,
  editable = false,
  canOpen = true,
  showsRetainer = true,
}: {
  period: IncidentPeriod;
  /** The product's level, passed down rather than fetched again: the page above already has it. */
  supportLevel?: SupportLevelValue | null;
  /** Off at every mount today: writing is done from the incident's own page, and the queue reaches it. */
  editable?: boolean;
  /** Off for a client: an incident's own page is in our Incidents section, which they cannot reach. */
  canOpen?: boolean;
  /** Off for a client: the retainer is our price, not something they bought on this page. */
  showsRetainer?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId();
  const transitionNavigate = useViewTransitionNav();
  const [draft, setDraft] = useState<IncidentDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [resolving, setResolving] = useState<IncidentView | null>(null);
  const [closing, setClosing] = useState<IncidentView | null>(null);

  const { data: incidentData, isLoading } = useProductIncidents(productId);
  const createIncident = useCreateIncident();
  const updateIncident = useUpdateIncident();
  const closeIncident = useCloseIncident();
  const reopenIncident = useReopenIncident();

  const incidents = useMemo(() => (incidentData?.items ?? []).map(incidentFromApi), [incidentData?.items]);
  const agreement = useMemo(() => agreementForLevel(supportLevel), [supportLevel]);
  const view = useMemo(() => buildIncidentsView(incidents, agreement, period), [incidents, agreement, period]);
  /** Unlike the organisation-wide page, this stays inside the quarter on both sides. */
  const { open, resolved } = useMemo(() => splitIncidents(view.incidents), [view]);

  const periodLabel = t('incidents.quarterOf', { quarter: period.quarter, year: period.year });

  if (isLoading) return <Loader />;

  // Nothing to judge against and nothing logged either, which is the one case with nothing to show.
  if (!agreement && !incidents.length) {
    return (
      <section>
        <SectionHeading>{t('incidents.section')}</SectionHeading>
        <p className="rounded-xl border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
          {t('incidents.noneLoggedNoAgreement')}
        </p>
      </section>
    );
  }

  // A refused write leaves the log as it was, so say so rather than closing the editor.
  const onFailed = (): void => setSaveFailed(true);

  const save = (): void => {
    if (!draft || !productId) return;
    const values = incidentInputFromDraft(draft);
    const done = { onSuccess: () => setDraft(null), onError: onFailed };

    if (isNew) {
      createIncident.mutate({ productId, values }, done);
      return;
    }
    updateIncident.mutate({ productId, incidentId: draft.id, values }, done);
  };

  /** Moving the record along from the row, on our copy of the tab only: the client's is a report they read. */
  const confirmResolve = (atLocal: string, alsoClose: boolean): void => {
    if (!productId || !resolving) return;
    const target = { productId, incidentId: resolving.id };
    const done = { onSuccess: () => setResolving(null), onError: onFailed };

    // One write, the close route carrying the moment: see the note on the same pair in `Incidents`.
    if (alsoClose) closeIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, done);
    else updateIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, done);
  };

  const confirmClose = (): void => {
    if (!productId || !closing) return;
    closeIncident.mutate(
      { productId, incidentId: closing.id, values: {} },
      { onSuccess: () => setClosing(null), onError: onFailed },
    );
  };

  const reopen = (incident: IncidentView): void => {
    if (!productId) return;
    reopenIncident.mutate({ productId, incidentId: incident.id }, { onError: onFailed });
  };

  const openIncident = (row: IncidentRow): void => {
    if (organizationId) transitionNavigate(incidentPath(organizationId, productId ?? '', row.incident.id));
  };

  const rowActions = (row: IncidentRow): ReactElement => (
    <IncidentRowActions
      incident={row.incident}
      onResolve={() => setResolving(row.incident)}
      onClose={() => setClosing(row.incident)}
      onReopen={() => reopen(row.incident)}
    />
  );

  return (
    <section className="flex flex-col gap-4">
      {/* The picker states the quarter on screen but is hidden in print, so the PDF says it here. */}
      <SectionHeading>
        {t('incidents.section')}
        <span className="hidden print:inline">{` · ${periodLabel}`}</span>
      </SectionHeading>

      {agreement && <AgreementCard agreement={agreement} showsRetainer={showsRetainer} />}

      {/* Straight under the agreement: what is still running is the only part of this tab to act on. */}
      {(open.length > 0 || editable) && (
        <Card>
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-base font-semibold text-foreground">
                {t('incidents.openSection', { total: open.length })}
              </p>
              {!open.length && (
                <p className="text-xs text-muted-foreground">{t('incidents.emptyOpen', { period: periodLabel })}</p>
              )}
            </div>
            {editable && (
              <Button
                variant="outline"
                size="sm"
                className="print:hidden"
                onClick={() => {
                  setIsNew(true);
                  // Neither is asked for on the client's form: both are answered by the act of filing it.
                  setDraft(emptyDraft());
                }}
              >
                <Plus className="size-4" />
                {t('incidents.logIncident')}
              </Button>
            )}
          </div>

          {open.length > 0 && (
            <OpenIncidentTable
              rows={open.map(incident => ({ incident, productId }))}
              showProduct={false}
              // The same duration columns as the resolved table, so the quarter reads as one report.
              outcomes
              onOpen={canOpen ? openIncident : undefined}
              actions={editable ? rowActions : undefined}
            />
          )}
        </Card>
      )}

      <Card>
        <p className="mb-4 text-base font-semibold text-foreground">
          {t('incidents.resolvedSection', { total: resolved.length })}
        </p>

        {resolved.length ? (
          <ResolvedIncidentTable
            rows={resolved.map(incident => ({ incident, productId }))}
            showProduct={false}
            // Both halves lead to the incident's page: a finished line is as worth reading as a running one.
            onOpen={canOpen ? openIncident : undefined}
            actions={editable ? rowActions : undefined}
          />
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('incidents.emptyResolved', { period: periodLabel })}
          </p>
        )}

        {saveFailed && <p className="mt-3 text-[11px] text-destructive">{t('incidents.saveFailed')}</p>}

        {/* The two things on this tab that are proposals rather than facts. Said on the tab, not just in
            the code, because they are what the review of it has to settle. A caveat is enough for a PM
            reading it; a client reading their own agreement needs the figures to be real first. */}
        <p className="mt-4 text-[11px] text-muted-foreground/80">
          {t(editable ? 'incidents.mockNoteEditable' : 'incidents.mockNote')}
        </p>
      </Card>

      <IncidentResolveDialog incident={resolving} onResolve={confirmResolve} onCancel={() => setResolving(null)} />

      <IncidentCloseDialog incident={closing} onClose={confirmClose} onCancel={() => setClosing(null)} />

      <IncidentEditor
        draft={draft}
        agreement={agreement}
        isNew={isNew}
        onChange={setDraft}
        onSave={save}
        onCancel={() => setDraft(null)}
      />
    </section>
  );
};

export default IncidentsTab;
